/**
 * OutputGenerator.tsx — Documents
 *
 * The three deliverables the mapping produces:
 * - the mapping workbook (Excel download)
 * - the BRD narrative (markdown, rendered inline)
 * - the FRD for the configuration team (markdown, rendered inline)
 */

import React, { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  ConversionProject,
  exportMappingTable,
  exportBRD,
  exportFRD,
  downloadBlob,
} from '../../services/conversionArchitectAPI';
import {
  c, font, eyebrow, panel, btnPrimary, btnSecondary,
} from './tokens';
import Toast from './Toast';

interface OutputGeneratorProps {
  project: ConversionProject;
}

interface DocState {
  content: string;
  sections: Array<string | { id?: string; title?: string }>;
  generated_at?: string;
}

function sectionTitle(section: string | { id?: string; title?: string }): string {
  if (typeof section === 'string') return section;
  return section.title || section.id || '';
}

// Rendering is done with react-markdown (remark-based). The previous
// implementation used marked's regex parser, which locked the browser's main
// thread on a real generated BRD — do not reintroduce it.
const MAX_RENDER_CHARS = 500_000;

// Prose styling for the rendered markdown — scoped with a data attribute so
// no stylesheet classes are introduced.
const PROSE_CSS = `
  [data-ca-doc-prose] h1 { font-size: 22px; font-weight: 800; color: #0F172A; margin: 26px 0 10px; }
  [data-ca-doc-prose] h2 { font-size: 18px; font-weight: 800; color: #0F172A; margin: 22px 0 8px; }
  [data-ca-doc-prose] h3 { font-size: 15px; font-weight: 800; color: #0F172A; margin: 18px 0 6px; }
  [data-ca-doc-prose] h4 { font-size: 13.5px; font-weight: 700; color: #0F172A; margin: 14px 0 6px; }
  [data-ca-doc-prose] p, [data-ca-doc-prose] li { font-size: 13px; color: #334155; line-height: 1.7; }
  [data-ca-doc-prose] ul, [data-ca-doc-prose] ol { padding-left: 22px; margin: 8px 0; }
  [data-ca-doc-prose] code { font-family: 'JetBrains Mono', ui-monospace, monospace; font-size: 12px; background: #F1F5F9; padding: 1px 5px; border-radius: 4px; color: #0F172A; }
  [data-ca-doc-prose] pre { background: #F8FAFC; border: 1px solid #E2E8F0; border-radius: 8px; padding: 12px 14px; overflow-x: auto; }
  [data-ca-doc-prose] pre code { background: transparent; padding: 0; }
  [data-ca-doc-prose] table { border-collapse: collapse; width: 100%; margin: 12px 0; }
  [data-ca-doc-prose] th, [data-ca-doc-prose] td { border: 1px solid #E2E8F0; padding: 6px 10px; font-size: 12.5px; color: #334155; text-align: left; }
  [data-ca-doc-prose] th { background: #F8FAFC; font-weight: 700; color: #475569; }
  [data-ca-doc-prose] blockquote { border-left: 3px solid #CBD5E1; margin: 10px 0; padding: 2px 14px; color: #64748B; }
  [data-ca-doc-prose] hr { border: none; border-top: 1px solid #F1F5F9; margin: 18px 0; }
`;

const OutputGenerator: React.FC<OutputGeneratorProps> = ({ project }) => {
  const [exporting, setExporting] = useState<'excel' | 'brd' | 'frd' | null>(null);
  const [brdDoc, setBrdDoc] = useState<DocState | null>(null);
  const [frdDoc, setFrdDoc] = useState<DocState | null>(null);
  const [activeDoc, setActiveDoc] = useState<'brd' | 'frd' | null>(null);
  const [toast, setToast] = useState('');

  const handleExportExcel = async () => {
    setExporting('excel');
    try {
      const blob = await exportMappingTable(project.id);
      const filename = `${project.name.replace(/[^a-zA-Z0-9]/g, '_')}_mapping_workbook.xlsx`;
      downloadBlob(blob, filename);
      setToast('Mapping workbook downloaded');
    } catch (err: any) {
      console.error('Excel export failed:', err);
      setToast(err.response?.data?.error || 'The workbook could not be generated');
    } finally {
      setExporting(null);
    }
  };

  const handleExportBRD = async () => {
    setExporting('brd');
    try {
      const data = await exportBRD(project.id);
      setBrdDoc({ content: data.content, sections: data.sections || [], generated_at: data.generated_at });
      setActiveDoc('brd');
      setToast('BRD narrative generated');
    } catch (err: any) {
      console.error('BRD generation failed:', err);
      setToast(err.response?.data?.error || 'The BRD could not be generated');
    } finally {
      setExporting(null);
    }
  };

  const handleExportFRD = async () => {
    setExporting('frd');
    try {
      const data = await exportFRD(project.id);
      setFrdDoc({ content: data.content, sections: [] });
      setActiveDoc('frd');
      setToast('FRD generated');
    } catch (err: any) {
      console.error('FRD generation failed:', err);
      setToast(err.response?.data?.error || 'The FRD could not be generated');
    } finally {
      setExporting(null);
    }
  };

  const cards: Array<{
    key: 'excel' | 'brd' | 'frd';
    icon: string;
    title: string;
    audience: string;
    body: string;
    action: () => void;
    actionLabel: string;
    busyLabel: string;
    doc?: DocState | null;
    docKey?: 'brd' | 'frd';
  }> = [
    {
      key: 'excel',
      icon: '📊',
      title: 'Mapping workbook — Excel',
      audience: 'For the conversion team preparing the Oracle Cloud load files.',
      body: 'The full account mapping as a workbook: every old account, its destination, what changes, and the approval status.',
      action: handleExportExcel,
      actionLabel: 'Download the workbook (.xlsx)',
      busyLabel: 'Building the workbook…',
    },
    {
      key: 'brd',
      icon: '📄',
      title: 'BRD narrative',
      audience: 'Signed off by Controllers and the business stakeholders.',
      body: 'The business story of the conversion: what changes, why, the decisions made, and the evidence from the checks.',
      action: handleExportBRD,
      actionLabel: brdDoc ? 'Regenerate the BRD' : 'Generate the BRD',
      busyLabel: 'Writing the BRD…',
      doc: brdDoc,
      docKey: 'brd',
    },
    {
      key: 'frd',
      icon: '🔧',
      title: 'FRD',
      audience: 'Goes to the downstream development and configuration teams.',
      body: 'The technical specification: field-level mappings, transformation rules, test scenarios, and configuration parameters.',
      action: handleExportFRD,
      actionLabel: frdDoc ? 'Regenerate the FRD' : 'Generate the FRD',
      busyLabel: 'Writing the FRD…',
      doc: frdDoc,
      docKey: 'frd',
    },
  ];

  const shownDoc = activeDoc === 'brd' ? brdDoc : activeDoc === 'frd' ? frdDoc : null;
  const shownTitle = activeDoc === 'brd'
    ? `BRD narrative — ${project.name}`
    : `FRD — ${project.name}`;

  return (
    <div style={{ fontFamily: font.ui }}>
      <style>{PROSE_CSS}</style>

      {/* Deliverable cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 14 }}>
        {cards.map(card => (
          <div key={card.key} style={{ ...panel, display: 'flex', flexDirection: 'column' }}>
            <div style={{ fontSize: 26, marginBottom: 10 }} aria-hidden="true">{card.icon}</div>
            <h3 style={{ margin: '0 0 4px', fontSize: 15, fontWeight: 800, color: c.textPrimary }}>
              {card.title}
            </h3>
            <div style={{ fontSize: 11.5, color: c.textSecondary, fontWeight: 600, marginBottom: 8 }}>
              {card.audience}
            </div>
            <p style={{ margin: '0 0 16px', fontSize: 12.5, color: c.textBody, lineHeight: 1.6, flex: 1 }}>
              {card.body}
            </p>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button
                type="button"
                style={btnPrimary(exporting === card.key)}
                onClick={card.action}
                disabled={exporting === card.key}
              >
                {exporting === card.key ? card.busyLabel : card.actionLabel}
              </button>
              {card.doc && card.docKey && (
                <button
                  type="button"
                  style={btnSecondary()}
                  onClick={() => setActiveDoc(activeDoc === card.docKey ? null : card.docKey!)}
                >
                  {activeDoc === card.docKey ? 'Hide' : 'View'}
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Document viewer */}
      {activeDoc && shownDoc && (
        <div style={{ marginTop: 20 }}>
          <div
            style={{
              background: c.card,
              border: `1px solid ${c.border}`,
              borderRadius: 12,
              maxWidth: 820,
              margin: '0 auto',
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 12,
                padding: '14px 24px',
                borderBottom: `1px solid ${c.hairline}`,
              }}
            >
              <div>
                <div style={{ fontSize: 15, fontWeight: 800, color: c.textPrimary }}>{shownTitle}</div>
                {shownDoc.generated_at && (
                  <div style={{ fontFamily: font.mono, fontSize: 11, color: c.textSecondary, marginTop: 2 }}>
                    Generated {new Date(shownDoc.generated_at).toLocaleString()}
                  </div>
                )}
              </div>
              <button type="button" style={btnSecondary()} onClick={() => setActiveDoc(null)}>
                Close
              </button>
            </div>

            {/* Small table of contents if the server names sections */}
            {shownDoc.sections.length > 0 && (
              <div style={{ padding: '14px 24px', borderBottom: `1px solid ${c.hairline}`, background: c.fill1 }}>
                <div style={{ ...eyebrow, marginBottom: 8 }}>In this document</div>
                <ol style={{ margin: 0, paddingLeft: 20 }}>
                  {shownDoc.sections.map((section, i) => (
                    <li key={i} style={{ fontSize: 12.5, color: c.textBody, lineHeight: 1.8 }}>
                      {sectionTitle(section)}
                    </li>
                  ))}
                </ol>
              </div>
            )}

            <div
              data-ca-doc-prose="true"
              style={{ padding: '24px 32px 36px', fontFamily: font.ui }}
            >
              {(shownDoc.content || '').length > MAX_RENDER_CHARS ? (
                <>
                  <p style={{ fontSize: 12, color: c.textSecondary }}>
                    This document is too large to render formatted — showing plain text.
                  </p>
                  <pre style={{ whiteSpace: 'pre-wrap', fontSize: 12 }}>{shownDoc.content}</pre>
                </>
              ) : (
                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                  {shownDoc.content || ''}
                </ReactMarkdown>
              )}
            </div>
          </div>
        </div>
      )}

      <Toast message={toast} onClose={() => setToast('')} />
    </div>
  );
};

export default OutputGenerator;
